import _get from 'lodash/get';
import { call, put, select } from 'redux-saga/effects';

import RavenError from 'ravenjs/lib/Error';

import { MFA_ERROR_TYPES, MFA_METHODS, MFA_ROUTES, MFA_STATUS } from 'constants/mfa';
import { catchError } from 'utils/errors';
import { verifyMFACodeAPI } from 'modules/apis/mfa';
import * as authActions from 'modules/auth/actions';
import { _loginWithAccessTokenSaga, _postLoginRedirectSaga } from 'modules/auth/sagas/login';
import { selectors as routerSelectors } from 'modules/router';
import history from 'store/history';

import * as mfaActions from '../actions';
import * as mfaSelectors from '../reducer';

const LOGIN_ROUTE = '/auth/login';

/**
 * Verify the MFA code for the current MFA session and, on success, complete
 * the regular sign in flow with the access token returned by the API. A code
 * that completes an enrollment (`MFA_REQUIRED` sessions) makes no other request:
 * the token set is held in memory and the success screen is rendered, the sign
 * in being completed by `completeMFAEnrollmentSaga` once the user leaves it.
 *
 * NOTE: the error is stored as is, the verify identity screen maps its
 * `errorCode` to the error state it renders. A verification attempted without
 * an MFA session never reaches the API, so it carries the error state to render
 * itself, i.e. the expired session the missing `mfaToken` stands for.
 *
 * @method verifyMFACodeSaga
 * @type   {Generator}
 * @param  {Object}          action The action holding the code to verify
 */
export function* verifyMFACodeSaga(action = {}) {
    try {
        yield put(mfaActions.verifyMFACode.request());

        const code = _get(action, 'payload.code', '');

        // The signed in user activates the method from the account settings
        // with the access token, there is no sign in to complete.
        if (_get(action, 'payload.isSettings', false)) {
            const method = _get(action, 'payload.method', '') || MFA_METHODS.TOTP;

            yield call(verifyMFACodeAPI, { code, method });
            yield put(mfaActions.verifyMFACode.success());

            return;
        }

        const mfaToken = yield select(mfaSelectors.getMFAToken);

        if (!mfaToken) {
            const noSessionError = new RavenError('There is no MFA session in progress.');

            noSessionError.mfaErrorType = MFA_ERROR_TYPES.SESSION_EXPIRED;

            throw noSessionError;
        }

        const enrolledMethod = yield select(mfaSelectors.getMFAMethod);
        const method = _get(action, 'payload.method', '') || enrolledMethod || MFA_METHODS.TOTP;
        const loginContext = yield select(mfaSelectors.getMFALoginContext);

        const response = yield call(verifyMFACodeAPI, { code, method, mfaToken });
        const token = _get(response, 'data', {});
        const accessToken = _get(token, 'access_token', '');

        if (!accessToken) {
            throw new RavenError('MFA verification failed.');
        }

        const status = yield select(mfaSelectors.getMFAStatus);

        if (status === MFA_STATUS.MFA_REQUIRED) {
            // The verified code activated the method, the success screen
            // confirms it before the sign in is completed. The current
            // `search`, i.e. the `from` deep link, travels along.
            yield put(mfaActions.verifyMFACode.success({ token }));

            const location = yield select(routerSelectors.getLocation);

            yield call([history, history.replace], {
                pathname: MFA_ROUTES.ENROLLMENT_SUCCESS,
                search: _get(location, 'search', ''),
            });

            return;
        }

        // Store the token the same way the regular sign in flow does,
        // so every following request is authorized with the access token.
        yield put(authActions.fetchToken.success(token));

        const loginResult = yield call(_loginWithAccessTokenSaga, accessToken);
        catchError(loginResult);

        // The challenge is over, the `mfaToken` is dropped from state.
        yield put(mfaActions.verifyMFACode.success());
        yield put(mfaActions.clearMFASession());
        yield call(_postLoginRedirectSaga, loginContext);
    } catch (error) {
        yield put(mfaActions.verifyMFACode.error(error));
    }
}

/**
 * Leave the enrollment success screen, i.e. sign in with the token set the
 * verification answered with and redirect where the enrollment was started
 * from: the `from` deep link of the current location wins, the login context
 * comes next and the app home is the fallback.
 *
 * NOTE: the token set only lives in memory, so without one (a reload of the
 * success screen) or when the sign in fails the user is sent back to sign in.
 *
 * @method completeMFAEnrollmentSaga
 * @type   {Generator}
 */
export function* completeMFAEnrollmentSaga() {
    try {
        yield put(mfaActions.completeMFAEnrollment.request());

        const token = yield select(mfaSelectors.getMFAPendingToken);
        const accessToken = _get(token, 'access_token', '');

        if (!accessToken) {
            throw new RavenError('There is no completed MFA enrollment to sign in with.');
        }

        const loginContext = yield select(mfaSelectors.getMFALoginContext);

        yield put(authActions.fetchToken.success(token));

        const loginResult = yield call(_loginWithAccessTokenSaga, accessToken);
        catchError(loginResult);

        yield put(mfaActions.completeMFAEnrollment.success());
        yield call(_postLoginRedirectSaga, loginContext);
        yield put(mfaActions.clearMFASession());
    } catch (error) {
        yield put(mfaActions.completeMFAEnrollment.error(error));
        yield put(mfaActions.clearMFASession());
        yield call([history, history.replace], LOGIN_ROUTE);
    }
}

export default verifyMFACodeSaga;
